import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useLocale } from 'hooks';
import { useFormContext } from 'react-hook-form';

import { StepContainer, IconTile, Checkbox, Button } from 'ui';
import { ScanField } from 'form/fields';
import { CheckboxIcon, CheckboxIconBorders, ErrorIcon, FluviusIcon, SendIcon, SuccessIcon } from 'ui/icons';
import { useAssets } from '../../hooks/useAssets/useAssets';

import { StepFormOwnerProps } from './StepFormOwner.types';
import { useStyles } from './StepFormOwner.styles';

export const StepFormOwner = ({ goToNextStep, goToPreviousStep, onSubmit }: StepFormOwnerProps) => {
  const {
    container,
    sendIcon,
    fluviusIcon,
    labelSubtitle,
    typographyContainer,
    statusText,
    buttonsContainer,
    tileContainer,
  } = useStyles();
  const { setValue, watch } = useFormContext();
  const [acceptedAgreement, setAcceptedAgreement] = useState(false);
  const [installationFound, setInstallationFound] = useState(false);
  const fields = watch(['id', 'meterSerial']);
  const { getAssetById } = useAssets();
  const { formatMessage } = useLocale();
  const theme = useTheme();

  const asset = getAssetById(fields.id);

  useEffect(() => {
    if (fields.id && fields.meterSerial === asset?.digitalMeter) {
      setInstallationFound(true);
    } else {
      setInstallationFound(false);
    }
  }, [fields, asset]);

  const handleSubmit = () => {
    onSubmit();
    goToNextStep();
  };

  const renderInstallationFoundView = () => (
    <>
      <Grid container className={typographyContainer} spacing={1}>
        <Grid container item xs={12} direction="row" alignItems="center">
          <Grid xs={1}>
            <SuccessIcon />
          </Grid>
          <Grid xs={11}>
            <Typography className={statusText}>
              {formatMessage({ id: 'activateAsset.steps.installationFound' })}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid container className={typographyContainer} spacing={1}>
        <Grid container item xs={12} direction="row" alignItems="center">
          <Checkbox
            icon={<CheckboxIconBorders />}
            checkedIcon={<CheckboxIcon />}
            color="primary"
            label={formatMessage({ id: 'activateAsset.steps.installationAgreement' })}
            checked={acceptedAgreement}
            onChange={() => setAcceptedAgreement(prevState => !prevState)}
          />
          <Typography className={labelSubtitle}>
            {formatMessage({ id: 'activateAsset.steps.installationLegalNotice' })}
          </Typography>
          <FluviusIcon className={fluviusIcon} />
        </Grid>
      </Grid>
      <Grid container spacing={1} className={tileContainer}>
        <Grid container item xs={12} justify="center">
          <IconTile
            backgroundColor={acceptedAgreement ? theme.palette.secondary.main : theme.palette.gray.dark}
            color={theme.palette.text.primary}
            title={formatMessage({ id: 'activateAsset.steps.reportInstallation' })}
            icon={<SendIcon classes={{ root: sendIcon }} />}
            onClick={acceptedAgreement ? handleSubmit : () => null}
          />
        </Grid>
      </Grid>
    </>
  );

  const renderInstallationNotFoundView = () => (
    <>
      <Grid container className={typographyContainer} spacing={1}>
        <Grid container item xs={12} direction="row" alignItems="center">
          <Grid xs={1}>
            <ErrorIcon />
          </Grid>
          <Grid xs={11}>
            <Typography className={statusText}>
              {formatMessage({ id: 'activateAsset.steps.installationNotFound' })}
            </Typography>
          </Grid>
          <Grid xs={12} alignItems="center">
            <Typography className={labelSubtitle}>
              {formatMessage({ id: 'activateAsset.steps.installationNotFoundNotice' })}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2} className={buttonsContainer}>
        <Grid item xs={6}>
          <Button variant="outlined" color="primary" onClick={goToPreviousStep}>
            {formatMessage({ id: 'common.back' })}
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button disabled variant="contained" color="primary" onClick={goToNextStep}>
            {formatMessage({ id: 'common.continue' })}
          </Button>
        </Grid>
      </Grid>
    </>
  );

  return (
    <StepContainer title={formatMessage({ id: 'activateAsset.steps.enterDetails' })}>
      <Grid container spacing={1} className={container}>
        <Grid item xs={12}>
          <ScanField
            onScan={value => setValue('id', value)}
            label={formatMessage({ id: 'activateAsset.steps.assetSerial' })}
            name="id"
          />
        </Grid>
        <Grid item xs={12}>
          <ScanField
            onScan={value => setValue('meterSerial', value)}
            label={formatMessage({ id: 'activateAsset.steps.meterSerial' })}
            name="meterSerial"
          />
        </Grid>
      </Grid>
      {installationFound ? renderInstallationFoundView() : renderInstallationNotFoundView()}
    </StepContainer>
  );
};
